import { useEffect, useState } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAdminGuard } from '../../hooks/useAdminGuard';

const Volunteers = () => {
  const { user, loading, isAdmin } = useAdminGuard({ redirectIfNotAdmin: true });
  const [volunteers, setVolunteers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sort, setSort] = useState<'name' | 'location'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (!user || !isAdmin) return;
    const fetchVolunteers = async () => {
      const querySnapshot = await getDocs(collection(db, 'volunteerApplications'));
      const volunteersList: any[] = [];
      querySnapshot.forEach((docSnap) => {
        volunteersList.push({ ...(docSnap.data()), id: docSnap.id });
      });
      setVolunteers(volunteersList);
    };
    fetchVolunteers();
  }, [user, isAdmin]);

  // Filtering, searching, sorting
  let filtered = volunteers.filter(v =>
    (!typeFilter || v.professionType === typeFilter) &&
    (!search ||
      v.name?.toLowerCase().includes(search.toLowerCase()) ||
      v.email?.toLowerCase().includes(search.toLowerCase()) ||
      v.phone?.toLowerCase().includes(search.toLowerCase()) ||
      v.profession?.toLowerCase().includes(search.toLowerCase()) ||
      v.location?.toLowerCase().includes(search.toLowerCase())
    )
  );
  if (sort === 'name') {
    filtered = filtered.sort((a, b) => {
      return sortDir === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    });
  } else if (sort === 'location') {
    filtered = filtered.sort((a, b) => {
      return sortDir === 'asc'
        ? a.location.localeCompare(b.location)
        : b.location.localeCompare(a.location);
    });
  }

  if (loading || !user || !isAdmin) return (
    <div className="flex justify-center items-center h-40">
      <svg className="animate-spin h-8 w-8 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <span className="ml-3 text-teal-700 text-sm">Loading volunteers...</span>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto py-6 px-1 sm:py-10 sm:px-4">
      <h1 className="text-xl xs:text-2xl sm:text-3xl font-bold text-teal-700 mb-4 xs:mb-6 sm:mb-8">Volunteer Applications</h1>
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
        <input
          type="text"
          placeholder="Search by name, email, phone, profession, or location"
          className="border border-stone-300 rounded px-3 py-2 text-sm w-full sm:w-64"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="border border-stone-300 rounded px-3 py-2 text-sm w-full sm:w-48"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
        >
          <option value="">All Types</option>
          <option value="medical">Medical</option>
          <option value="non-medical">Non-Medical</option>
        </select>
        <div className="flex gap-2 items-center">
          <label className="text-xs text-stone-500">Sort by:</label>
          <select
            className="border border-stone-300 rounded px-2 py-1 text-xs"
            value={sort}
            onChange={e => setSort(e.target.value as 'name' | 'location')}
          >
            <option value="name">Name</option>
            <option value="location">Location</option>
          </select>
          <button
            className="ml-1 px-2 py-1 rounded border border-stone-300 text-xs hover:bg-stone-100"
            onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg shadow border border-stone-200 bg-white">
        <table className="min-w-[500px] xs:min-w-[600px] sm:min-w-[700px] md:min-w-full divide-y divide-stone-200 text-[11px] xs:text-xs sm:text-sm">
          <thead className="bg-stone-50">
            <tr>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Name</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Email</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Phone</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Type</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Profession</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Location</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">How to Help</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-stone-100">
            {filtered.map((v) => (
              <tr key={v.id} className="hover:bg-stone-50">
                <td className="px-2 py-2 sm:px-4 sm:py-3 font-medium text-stone-800 break-all">{v.name}</td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-600 break-all">{v.email}</td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-600 break-all">
                  <a href={`tel:${v.phone}`} className="text-teal-700 hover:underline">{v.phone}</a>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-700">{v.professionType}</td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-700">{v.profession}</td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-700">{v.location}</td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-700">{v.howToHelp}</td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 flex gap-2">
                  <a href={`mailto:${v.email}`} className="text-blue-600 hover:underline text-xs sm:text-sm">Email</a>
                  <a href={`tel:${v.phone}`} className="text-teal-700 hover:underline text-xs sm:text-sm">Call</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Volunteers; 